// src/hooks/useAiChat.js
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * scope:
 *  - "landing"
 *  - "dashboard"
 *  - "group_member"
 *  - "group_admin"
 */
const WELCOME_MESSAGES = {
  landing:
    "Hi! I’m the ZiiHub assistant 🤝 I can explain how ZiiHub works and help you see if it’s a good fit for your community.",
  dashboard:
    "Hey! I’m your ZiiHub assistant. I can help you understand your groups, find interesting communities, and answer questions about the platform.",
  group_member:
    "Welcome to this group 👋 I can answer questions about activities, events, and how this community works.",
  group_admin:
    "Hi Admin! I can help you manage events, emails, products, and more. Ask me what you’d like to do and I’ll draft actions for you to review.",
};

function buildMessagesForApi(messagesWithUser) {
  return messagesWithUser.map((m) => ({
    role: m.from === "user" ? "user" : "assistant",
    content: m.text,
  }));
}

export default function useAiChat({ scope = "dashboard", groupId = null }) {
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);

  /**
   * Reset conversation when scope/groupId changes
   */
  useEffect(() => {
    const key =
      scope === "landing"
        ? "landing"
        : scope === "dashboard"
        ? "dashboard"
        : scope === "group_admin"
        ? "group_admin"
        : "group_member";

    const welcomeText = WELCOME_MESSAGES[key];

    setMessages([
      {
        id: `welcome-${key}-${groupId || "global"}`,
        from: "bot",
        text: welcomeText,
      },
    ]);
  }, [scope, groupId]);

  /**
   * Send a message
   */
  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed) return null;

    // Push the user message immediately
    const userMsg = {
      id: `user-${Date.now()}`,
      from: "user",
      text: trimmed,
    };

    const conversationWithUser = [...messages, userMsg];
    setMessages(conversationWithUser);
    setSending(true);

    try {
      let replyText = "";
      let actionsSaved = [];

      // LANDING CHAT (no auth)
      if (scope === "landing") {
        const { data } = await api.post("/ai/landing-chat", {
          messages: buildMessagesForApi(conversationWithUser),
        });

        replyText =
          data?.reply ||
          "I’m here, but I couldn’t reach the assistant server right now.";
      } else {
        // AUTHENTICATED CHAT
        const payload = {
          scope,
          groupId,
          messages: buildMessagesForApi(conversationWithUser),
        };

        const { data } = await api.post("/ai/chat", payload);

        replyText =
          data?.reply ||
          "I’m here, but I couldn’t reach the assistant server right now.";

        // ⬇️ Detect admin actions (group_admin mode)
        if (Array.isArray(data?.actions_saved) && data.actions_saved.length) {
          actionsSaved = data.actions_saved;

          // Add a small summary for the assistant UI
          const summaryLine = `✨ I drafted ${actionsSaved.length} admin action${
            actionsSaved.length === 1 ? "" : "s"
          } for you to review in the AI actions panel.`;

          replyText = `${replyText}\n\n${summaryLine}`;

          // ⬇️ FIRE GLOBAL EVENT FOR DIALOG TO AUTO-OPEN
          if (
            typeof window !== "undefined" &&
            scope === "group_admin" &&
            groupId
          ) {
            window.dispatchEvent(
              new CustomEvent("zii:aiActionsDrafted", {
                detail: {
                  groupId,
                  count: actionsSaved.length,
                  actions: actionsSaved,
                },
              })
            );
          }
        }
      }

      // Push bot reply
      const botMsg = {
        id: `bot-${Date.now()}`,
        from: "bot",
        text: replyText,
      };

      setMessages((prev) => [...prev, botMsg]);
      return replyText;
    } catch (err) {
      console.error("AI chat error", err);
      toast.error("The assistant is temporarily unavailable. Please try again.");

      const errorMsg = {
        id: `bot-error-${Date.now()}`,
        from: "bot",
        text:
          "Sorry, I couldn’t reach the assistant server right now. Please try again in a moment.",
      };

      setMessages((prev) => [...prev, errorMsg]);
      return null;
    } finally {
      setSending(false);
    }
  };

  return {
    messages,
    sending,
    sendMessage,
  };
}
